import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import {
  triggerFromEvent,
  useCampaignSourceOptions,
  useCampaignUserOptions,
} from '#/features/campaign-enrol'
import { CampaignEnrollmentMode } from '#/shared/api/generated/communication/models'
import type { CampaignRead } from '#/shared/api/generated/communication/models'
import { useGetCampaignApiV1CampaignsCampaignIdGet } from '#/shared/api/generated/communication/theCargoCommunication'
import type { MultiSelectOption } from '#/shared/ui/multi-select'

import {
  optionNames,
  useCampaignJoinFilters,
} from './use-campaign-join-filters'
import type { CampaignJoinFilterChip } from './use-campaign-join-filters'

/** The one key the enroller fills in for itself when a definition omits it. */
const FILTER_STAGE_KEY = 'stage'
const FILTER_STAGE_DEFAULT = 'quote'

/** The saved enrolment rule, as the Strategy card's chips need it. */
export interface CampaignStrategySeed {
  mode: CampaignEnrollmentMode
  /** The definition the enroller runs — only read in `filter` mode. */
  filterDefinition: Record<string, unknown>
  /** Empty means every source, which is what a null `provider_ids` means. */
  providerIds: string[]
  /** Empty means every agent. */
  userIds: string[]
  triggerEvent: string | null
  /** The saved version the chips read. A later save changes it, and the row
   *  is keyed on it — see `CampaignStrategyChips`. */
  updatedAt: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null && !Array.isArray(value)
}

/**
 * The definition as the enroller applies it, not as it was stored: a missing
 * `stage` is not "any stage", it is the quote stage, so the card names it
 * rather than letting the reader assume the filter is wider than it is.
 */
function appliedDefinition(definition: unknown): Record<string, unknown> {
  const saved = isRecord(definition) ? definition : {}
  return FILTER_STAGE_KEY in saved
    ? saved
    : { ...saved, [FILTER_STAGE_KEY]: FILTER_STAGE_DEFAULT }
}

export function toCampaignStrategySeed(
  campaign: CampaignRead,
): CampaignStrategySeed {
  return {
    mode: campaign.enrollment_mode,
    filterDefinition: appliedDefinition(campaign.filter_definition),
    providerIds: campaign.provider_ids ?? [],
    userIds: campaign.applies_to_user_ids ?? [],
    triggerEvent: campaign.trigger_event ?? null,
    updatedAt: campaign.updated_at,
  }
}

/**
 * The saved rule behind the Strategy card. The overview's own `audience.chips`
 * carry the trigger event only, so the rule itself comes from
 * `GET /campaigns/{id}` — the same read the Sequence tab, the audience editor
 * and the attribution menu keep, so the tab pays for it once.
 */
export function useCampaignStrategySeed(campaignId: string) {
  const query = useGetCampaignApiV1CampaignsCampaignIdGet(campaignId)
  const campaign = query.data?.data
  const seed = useMemo(
    () => (campaign == null ? null : toCampaignStrategySeed(campaign)),
    [campaign],
  )
  return { seed, isLoading: query.isPending }
}

/** The event chip. An event the dialogs do not name reads as itself — the
 *  read model types it as a bare string, and the service may add more. */
function triggerChip(
  t: TFunction,
  event: string | null,
): CampaignJoinFilterChip[] {
  if (event == null) return []
  const trigger = triggerFromEvent(event)
  const value =
    trigger == null ? event : t(`campaigns.modal.join.triggers.${trigger}`)
  return [
    {
      key: 'trigger',
      label: t('campaigns.detail.strategy.trigger', { value }),
    },
  ]
}

/** An id filter reads as one chip of names; no ids means no narrowing, and a
 *  chip saying "all of them" is noise. */
function idChip(
  t: TFunction,
  key: 'sources' | 'agents',
  ids: string[],
  options: MultiSelectOption[],
): CampaignJoinFilterChip[] {
  if (ids.length === 0) return []
  return [
    {
      key,
      label: t(`campaigns.detail.strategy.${key}`, {
        value: optionNames(ids, options),
      }),
    },
  ]
}

interface CampaignStrategyChipsResult {
  chips: CampaignJoinFilterChip[]
  isLoading: boolean
}

/**
 * The campaign's own rule, as chips — and only the part of it the enroller
 * applies in the saved mode. A manual campaign has no rule to show (people
 * were added by hand). A filter campaign runs its `filter_definition` and its
 * sources. A trigger campaign runs its event, its sources and its agents; its
 * `filter_definition` is ignored, so the card does not claim it.
 */
export function useCampaignStrategyChips(
  seed: CampaignStrategySeed,
): CampaignStrategyChipsResult {
  const { t } = useTranslation()
  const isManual = seed.mode === CampaignEnrollmentMode.manual
  const isFilter = seed.mode === CampaignEnrollmentMode.filter
  const isTrigger = seed.mode === CampaignEnrollmentMode.trigger
  const filters = useCampaignJoinFilters(isFilter, seed.filterDefinition)
  // Names, not ids — and only for a list that actually narrows something.
  const sources = useCampaignSourceOptions(
    !isManual && seed.providerIds.length > 0,
  )
  const users = useCampaignUserOptions(isTrigger && seed.userIds.length > 0)

  const chips = useMemo(
    () =>
      isManual
        ? []
        : [
            ...(isTrigger ? triggerChip(t, seed.triggerEvent) : []),
            ...(isFilter ? filters.chips : []),
            ...idChip(t, 'sources', seed.providerIds, sources.options),
            ...(isTrigger
              ? idChip(t, 'agents', seed.userIds, users.options)
              : []),
          ],
    [
      t,
      seed,
      isManual,
      isFilter,
      isTrigger,
      filters.chips,
      sources.options,
      users.options,
    ],
  )

  return {
    chips,
    isLoading: filters.isLoading || sources.isLoading || users.isLoading,
  }
}
